// The Home "running low" tile: how many products are at or below their reorder point (per warehouse, the shared
// rule in lib/lowStock.js) and how many products have stock.
//
// It comes from ONE backend call (GET /api/home/inventory-tile) that reads the company's COMPLETE stock and rules.
// If that call is not available, for example a website deployed before its backend, the same numbers are computed
// here from paged reads (complete too, never the old silent stop at 1,000 rows), and a warning naming the route and
// the reason is written to the browser console.
import { pageAll } from './pageAll';
import { countLowStock } from './lowStock';

// The grouping Home always did, unchanged. Also the reference the tests compare the backend with.
export function tileFromRows(balances, rules) {
  const byProduct = new Map();
  for (const b of balances || []) {
    if (!b.products) continue;
    const it = byProduct.get(b.product_id) || { productId: b.product_id, sku: b.products.sku, availableNum: 0, sites: [] };
    const avail = Number(b.available_qty ?? b.on_hand_qty ?? 0);
    it.availableNum += avail;
    it.sites.push({ warehouseId: b.warehouse_id, availableNum: avail });
    byProduct.set(b.product_id, it);
  }
  return { runningLow: countLowStock(rules || [], [...byProduct.values()]), stockItems: byProduct.size };
}

const isCount = (v) => typeof v === 'number' && Number.isFinite(v);

// -> { runningLow, stockItems }, both null when it could not be read (a failed read shows nothing, never a 0).
export async function loadInventoryTile({ apiFetch, supabase, orgId }) {
  let reason;
  try {
    const res = await apiFetch('/api/home/inventory-tile');
    if (res.ok) {
      const j = await res.json();
      if (j && j.ok && isCount(j.running_low) && isCount(j.stock_items)) return { runningLow: j.running_low, stockItems: j.stock_items };
      reason = 'unexpected_answer';
    } else {
      reason = `status_${res.status}`;
    }
  } catch {
    reason = 'request_failed';
  }
  // eslint-disable-next-line no-console
  console.warn(`[fallback] route=/api/home/inventory-tile reason=${reason}`);
  const [balancesRes, rulesRes] = await Promise.all([
    pageAll(() => supabase
      .from('inventory_balances')
      .select('product_id, warehouse_id, on_hand_qty, available_qty, products(sku)')
      .eq('organization_id', orgId)
      .order('product_id', { ascending: true })
      .order('warehouse_id', { ascending: true })),
    pageAll(() => supabase
      .from('reorder_rules')
      .select('product_id, sku, warehouse_id, min_qty, is_active')
      .eq('organization_id', orgId)
      .order('id', { ascending: true })),
  ]);
  if (balancesRes.error || rulesRes.error) return { runningLow: null, stockItems: null };
  return tileFromRows(balancesRes.data, rulesRes.data);
}
